import React, {ChangeEvent, useState} from 'react';
import {ContactMember, Membership, MembershipData, UserMember} from "types/Membership";
import {Form, Formik, FormikHelpers, FormikProps} from "formik";
import Field from "components/Form/Field";
import FormControls from "components/FormControls";
import FieldHeader from "components/Form/FieldHeader";
import MoneyInput from "components/Form/MoneyInput";
import PaymentMethod from "types/PaymentMethod";
import {DateTime} from "luxon";
import {useQuery} from "react-query";
import ngage from 'services/ngage';
import Selector from "components/Form/Selector";
import {Item} from "components/Form/Selector/Selector";
import {User} from 'types/User';
import {Tab} from "@headlessui/react";
import TabButton from "components/TabButton";
import {toTitleCase} from "services/string";
import Select from 'components/Form/Select';
import IconName from "types/IconName";
import {Option} from "components/Form/Select/Select";
import TicketingSource from "../../../types/TicketingSource";
import Alert from "../../../components/Alert";
import {Contact} from "../../../types/Contact";
import AgeGroup from "../../../types/AgeGroup";
import {MembershipPlan} from "../../../types/MembershipPlan";
import useMoney, {DiscountMode} from "../../../hooks/useMoney";
import * as Yup from "yup";
import {ErrorNotification} from "../../../components/Form/ErrorNotification";
import FieldHelp from "../../../components/Form/Form/Field/FieldHelp";
import {useOperator} from "../../../context/OperatorContext";

// Constants ----

const paymentMethods: Array<Item> = [
  {id: PaymentMethod.NONE, name: 'None'},
  {id: PaymentMethod.CARD, name: 'Card'},
  {id: PaymentMethod.CASH, name: 'Cash'}
]

// Helpers ----

function userOption(user: User | UserMember): Option {
  return {
    type: 'user',
    id: user.id,
    name: user.name,
    description: user.email,
    icon: IconName.USER
  }
}

function contactOption(contact: Contact | ContactMember): Option {
  return {
    type: 'contact',
    id: contact.id,
    name: 'type' in contact ? contact.name : contact.full_name,
    icon: IconName.USER_CIRCLE
  }
}

function membersAsOptions(members: Array<UserMember | ContactMember>): Array<Option> {
  return members.map(member => member.type === 'user' ? userOption(member) : contactOption(member));
}

function calculateDiscount({price, discountAmount, discountPercent, discountMode}: CalculateDiscountOptions) {
  switch (discountMode) {
    case DiscountMode.PERCENT:
      return (price / 100) * discountPercent;
    case DiscountMode.AMOUNT:
      return discountAmount;
  }
}

async function addContact(ownerId: number, name: string): Promise<Option | null> {
  try {
    const nameParts = name.split(' ').filter(part => !!part)

    const contact = await ngage.contacts.store({
      owner_id: ownerId,
      first_name: nameParts[0],
      last_name: nameParts.slice(1).join(' '),
      age_group: AgeGroup.ADULT
    })

    return contact ? contactOption(contact) : null
  } catch (error) {
    return null
  }
}

// Types ----

type CalculateDiscountOptions = {
  price: number
  discountAmount: number
  discountPercent: number
  discountMode: DiscountMode
}

type Props = {
  membership?: Membership
  onSubmit: (values: MembershipData, helpers: FormikHelpers<MembershipData>) => any
}

// Component ----

function MembershipForm(props: Props) {
  const {termFor} = useOperator();
  const {formattedMoney} = useMoney();

  const [selectedPlan, setSelectedPlan] = useState<MembershipPlan | null>(props.membership?.plan || null);
  const [ownerId, setOwnerId] = useState<number | null>(props.membership?.owner?.id || null);
  const [selectedMembers, setSelectedMembers] = useState<Array<Option>>(membersAsOptions(props.membership?.members || []));
  const [discountMode, setDiscountMode] = useState<DiscountMode>(DiscountMode.PERCENT);

  const {
    data: plans,
    isLoading: plansLoading
  } = useQuery(['membership-plans', {page: 1}], () => ngage.membershipPlans.all(1))

  async function userSource(query: string) {
    return (await ngage.users.filtered({search: query || null}))?.data.map(userOption) || []
  }

  async function contactSource(query: string) {
    if (!ownerId) return []

    return (await ngage.contacts.filtered({owner: ownerId, search: query || null}))?.data.map(contactOption) || []
  }

  async function memberSource(query: string) {
    const [users, contacts] = await Promise.all([userSource(query), contactSource(query)])

    users.sort((a, b) => a.name.localeCompare(b.name))
    contacts.sort((a, b) => a.name.localeCompare(b.name))

    return [...contacts,...users]
  }

  function handleSelectPlan(plan: Item, formik: FormikProps<MembershipData>) {
    if (plans) {
      void formik.setFieldValue('plan_id', plan.id)
      setSelectedPlan(plans.data.find(p => p.id === Number(plan.id)) || null)
    }
  }

  function handleSelectValidAt(value: string, formik: FormikProps<MembershipData>) {
    void formik.setFieldValue('valid_at', DateTime.fromFormat(value, 'yyyy-LL-dd').toISO())
  }

  function handleSelectExpiresAt(value: string, formik: FormikProps<MembershipData>) {
    void formik.setFieldValue('expires_at', DateTime.fromFormat(value, 'yyyy-LL-dd').toISO())
  }

  function handleSelectOwner(owner: Option, formik: FormikProps<MembershipData>) {
    void formik.setFieldValue('owner_id', owner?.id ?? 0).then(_ => {
      void formik.validateField('owner_id');
    })
    setOwnerId(Number(owner.id))

    setSelectedMembers([owner]);
    void formik.setFieldValue('members', [{type: 'user', id: owner.id}])
  }

  function handleSelectMembers(members: Array<Option>, formik: FormikProps<MembershipData>) {
    void formik.setFieldValue('members', members.map(member => ({type: member.type, id: member.id})))
    setSelectedMembers(members)
  }

  function selectDiscountMode(index: number, {setFieldValue}: FormikHelpers<any>) {
    switch (index) {
      case DiscountMode.PERCENT:
        void setFieldValue('discount_percent', props.membership?.discount.percent);
        void setFieldValue('discount_amount', undefined);
        break;

      case DiscountMode.AMOUNT:
        void setFieldValue('discount_amount', props.membership?.discount.amount);
        void setFieldValue('discount_percent', undefined);
        break;
    }

    setDiscountMode(index);
  }

  const isExternal = props.membership && props.membership?.source.name !== TicketingSource.NGAGE;
  const isFree = !selectedPlan?.price;

  const validationSchema = Yup.object().shape({
    plan_id: Yup.number().notOneOf([0], 'This field is required').required("This field is required"),
    valid_at: Yup.date().required("This field is required"),
    expires_at: !props.membership
      ? Yup.date().optional()
      : Yup.date().when(['valid_at'], ([validAt]: any, schema: any) => {
        if (!validAt) return schema.required('This field is required');
        return schema.min(validAt, "Must not be before 'Valid at' date").required('This field is required');
      }),
    owner_id: Yup.number().notOneOf([0], 'This field is required').required("This field is required"),
  })

  const initialValues: MembershipData = {
    plan_id: props.membership?.plan.id || 0,
    discount_percent: props.membership?.discount.percent || 0,
    discount_amount: undefined,
    paid_amount: props.membership?.payment.amount || 0,
    payment_method: props.membership?.payment.method || PaymentMethod.NONE,
    valid_at: props.membership?.valid_at?.toLocal().toISO() ?? DateTime.now().toISO(),
    expires_at: props.membership?.expires_at?.toLocal().toISO() ?? undefined,
    owner_id: props.membership?.owner?.id || 0,
    members: props.membership?.members.map(member => ({type: member.type, id: member.id})) || []
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={props.onSubmit}
    >
      {formik => {
        const discountValue = calculateDiscount({
          price: selectedPlan?.price ?? 0,
          discountMode,
          discountAmount: formik.values.discount_amount ?? 0,
          discountPercent: formik.values.discount_percent ?? 0,
        });

        const planPrice = formattedMoney(selectedPlan?.price ?? 0, props.membership?.currency);
        const discountAmount = formattedMoney(discountValue ?? 0, props.membership?.currency);
        const paidValue = formik.values.paid_amount ?? 0;
        const amountDue = formattedMoney(((selectedPlan?.price ?? 0) - discountValue - paidValue) ?? 0, props.membership?.currency);

        return (
          <Form className="space-y-8 divide-y divide-gray-200">
            <ErrorNotification formik={formik} />

            <div className="shadow sm:rounded-md">
              <div className="px-4 py-5 bg-white sm:p-6 sm:rounded-md">

                {isExternal &&
                  <Alert
                    message={`This membership has been imported from ${toTitleCase(props.membership?.source.name ?? '')}, so some details cannot be changed here.`}/>
                }

                <div className="space-y-8 divide-y divide-gray-200">
                  <div>
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Key Information
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Basic details about the {termFor('membership')}
                      </p>
                    </div>
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">

                      {/* Plan */}
                      <div className="sm:col-span-3">
                        <Selector
                          label={`${termFor('Membership Plan')} *`}
                          isLoading={plansLoading}
                          items={plans?.data.map(plan => ({
                            id: plan.id,
                            name: plan.name,
                            description: `${plan.duration.toHuman()} for ${formattedMoney(plan.price, plan.currency)}`,
                            icon: 'color-swatch'
                          })) || []}
                          selectedId={formik.values.plan_id}
                          onChange={plan => handleSelectPlan(plan, formik)}
                          disabled={!!props.membership}
                        />

                        {(!!formik.errors?.plan_id && !!formik.touched?.plan_id) &&
                          <FieldHelp isError>
                            {formik.errors.plan_id}
                          </FieldHelp>
                        }
                      </div>

                      {/* Valid At */}
                      <div className="sm:col-span-3 sm:col-start-1 md:col-span-2 md:col-start-1">
                        <div className="mt-1">
                          <Field
                            type="date"
                            name="valid_at"
                            label="Valid At *"
                            id="valid_at"
                            disabled={isExternal}
                            value={DateTime.fromISO(formik.values.valid_at ?? '').toFormat('yyyy-LL-dd')}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleSelectValidAt(event.target.value, formik)}
                          />
                        </div>
                      </div>

                      {/* Expires At */}
                      {!!props.membership &&
                        <div className="sm:col-span-3 md:col-span-2">
                          <FieldHeader label="Expires At"/>
                          <div className="mt-1">
                            <Field
                              type="date"
                              name="expires_at"
                              id="expires_at"
                              disabled={isExternal}
                              value={DateTime.fromISO(formik.values.expires_at ?? '').toFormat('yyyy-LL-dd')}
                              onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleSelectExpiresAt(event.target.value, formik)}
                            />
                          </div>
                        </div>
                      }

                      {/* Owner */}
                      <div className="sm:col-span-3 sm:col-start-1">
                        <Select
                          label="Owner *"
                          hint="Type to search"
                          source={userSource}
                          selected={props.membership?.owner}
                          onChange={selected => handleSelectOwner(selected, formik)}
                          disabled={isExternal}
                        />

                        {(!!formik.errors?.owner_id && !!formik.touched?.owner_id) &&
                          <FieldHelp isError>
                            {formik.errors.owner_id}
                          </FieldHelp>
                        }
                      </div>

                    </div>
                  </div>

                  <div className="pt-8">
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Members
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        {selectedPlan && selectedPlan?.maximum_members !== 1 ?
                          `Choose up to ${selectedPlan?.maximum_members} people who can use this ${termFor('membership')}` :
                          `Choose which person can use this ${termFor('membership')}`
                        }
                      </p>
                    </div>
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">

                      {/* Members */}
                      <div className="sm:col-span-6">
                        <Select
                          label={selectedPlan && selectedPlan?.maximum_members !== 1 ? 'Members' : 'Member'}
                          hint="Type to search"
                          source={memberSource}
                          selected={selectedMembers}
                          onNew={ownerId ? name => addContact(ownerId, name) : undefined}
                          onChange={selected => handleSelectMembers(selected, formik)}
                          multiple
                          max={selectedPlan?.maximum_members}
                          required
                          key={ownerId}
                        />
                      </div>
                    </div>
                  </div>

                  {!isFree &&
                    <div className="pt-8">
                      <div>
                        <h3 className="text-lg leading-6 font-medium text-gray-900">
                          Discount
                        </h3>
                        <p className="mt-1 text-sm text-gray-500">
                          Apply discount on this {termFor('membership')}
                        </p>
                      </div>
                      <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">

                        {/* Discount Percent/Amount */}
                        <div className="sm:col-span-2">
                          <FieldHeader label="Discount"/>
                          <Tab.Group
                            selectedIndex={discountMode}
                            onChange={index => {
                              (!isExternal) && selectDiscountMode(index, formik)
                            }}
                          >
                            <Tab.List className="mt-1 mb-1 relative z-0 inline-flex shadow-sm rounded-md">
                              <TabButton position={TabButton.positions.FIRST} disabled={isExternal}>Percent</TabButton>
                              <TabButton position={TabButton.positions.LAST} disabled={isExternal}>Amount</TabButton>
                            </Tab.List>
                            <Tab.Panels>
                              <Tab.Panel>
                                <Field
                                  type="number"
                                  name="discount_percent"
                                  id="discount_percent"
                                  addon="%"
                                  min={0}
                                  max={100}
                                  step={0.01}
                                  disabled={isExternal}
                                  onBlur={({target}: ChangeEvent<HTMLInputElement>) => {
                                    if (target.value === '') void formik.setFieldValue('discount_percent', 0);
                                  }}
                                />
                              </Tab.Panel>
                              <Tab.Panel>
                                <MoneyInput
                                  currency={MoneyInput.Currency.GBP}
                                  value={formik.values?.discount_amount}
                                  onChange={amount => void formik.setFieldValue('discount_amount', amount || 0)}
                                  disabled={isExternal}
                                  min={0}
                                  max={selectedPlan?.price ? selectedPlan.price / 100 : 0}
                                />
                              </Tab.Panel>
                            </Tab.Panels>
                          </Tab.Group>
                        </div>
                      </div>
                    </div>
                  }

                  {!isFree &&
                    <div className="pt-8">
                      <div>
                        <h3 className="text-lg leading-6 font-medium text-gray-900">
                          Payment
                        </h3>
                        <p className="mt-1 text-sm text-gray-500">
                          Record payment towards this {termFor('membership')}
                        </p>

                        <p className="text-sm text-gray-700 mt-6 font-medium">
                          Plan price: {planPrice}
                        </p>

                        <p className="text-sm text-gray-700 mt-2 font-medium">
                          Discount: {discountAmount}
                        </p>

                        <p className="text-sm text-gray-700 mt-2 font-medium">
                          Amount Due: {amountDue}
                        </p>
                      </div>
                      <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">

                        {/* Paid */}
                        <div className="sm:col-span-2">
                          <FieldHeader label="Amount Paid"/>
                          <div className="mt-1">
                            <MoneyInput
                              currency={props.membership?.currency.symbol}
                              value={formik.values.paid_amount}
                              onChange={amount => formik.setFieldValue('paid_amount', amount || 0)}
                              disabled={isExternal}
                              min={0}
                              max={selectedPlan?.price ? selectedPlan.price / 100 : 0}
                            />
                          </div>
                        </div>

                        {/* Payment Method */}
                        <div className="sm:col-span-2">
                          <Selector
                            label="Payment Method"
                            items={paymentMethods}
                            selectedId={formik.values.payment_method}
                            onChange={item => formik.setFieldValue('payment_method', item.id)}
                            required={!!formik.values.paid_amount}
                            disabled={isExternal}
                          />
                        </div>
                      </div>
                    </div>
                  }
                </div>
              </div>

              <FormControls formik={formik}/>
            </div>
          </Form>
      )}}
    </Formik>
  );
}

export default MembershipForm;